import * as THREE from 'three';
import { createTravelDie } from './portalTravel';
import { getAttackDieFaceValues, getDie } from './dice';
import { createLivingHeart } from './coreLivingHeart';
import { createCoreDestruction } from './coreDestruction';
import { createCoreFinaleSky } from './coreFinaleSky';
import { observeElementSize } from '../../utils/observeElementSize';
import { createGuardianStrikeScene } from './guardianStrikeScene';

export const CORE_FINALE_DURATION_MS = 11800;
const smooth = (time, start, end) =>
  THREE.MathUtils.smoothstep(time, start, end);
export const getCoreFinaleFrame = (time) => ({
  charge: smooth(time, 0.5, 2.6),
  strike: smooth(time, 2.65, 3.25),
  crack: smooth(time, 3.2, 4.4),
  burst: smooth(time, 4.15, 6.3),
  restore: smooth(time, 5.6, 9.4),
  hero: smooth(time, 8.3, 9.8),
  flash: Math.exp(-Math.pow((time - 4.25) / 0.24, 2)) * 0.38,
  fade: smooth(time, 11.25, 11.8),
});

const glowMaterial = (color, opacity = 1) =>
  new THREE.MeshBasicMaterial({
    color,
    opacity,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
    side: THREE.DoubleSide,
  });

const makeGlow = () => {
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) {
      const offset = (y * size + x) * 4;
      data[offset] = data[offset + 1] = data[offset + 2] = 255;
      data[offset + 3] = Math.round(
        255 *
          Math.pow(Math.max(0, 1 - Math.hypot(x - 31.5, y - 31.5) / 32), 2.6)
      );
    }
  const texture = new THREE.DataTexture(data, size, size);
  texture.needsUpdate = true;
  return texture;
};

export const createCoreFinaleScene = (
  host,
  { dieId, damage, faceValues, reduceMotion }
) => {
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
  } catch {
    return null;
  }
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  host.appendChild(renderer.domElement);
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(44, 1, 0.1, 180);
  const sky = createCoreFinaleSky();
  scene.add(sky.mesh);
  scene.add(new THREE.HemisphereLight('#a9eaff', '#160717', 2.4));
  const key = new THREE.DirectionalLight('#fff2dd', 4);
  key.position.set(-3, 4, 6);
  scene.add(key);
  const coreLight = new THREE.PointLight('#ff326d', 8, 20, 2);
  coreLight.position.set(0, 0.5, 2);
  scene.add(coreLight);
  const skin = getDie(dieId);
  const enemy = new THREE.Group();
  enemy.position.y = 0.3;
  enemy.scale.setScalar(1.2);
  scene.add(enemy);
  const heart = createLivingHeart(enemy);
  const destruction = createCoreDestruction(heart.body, scene);
  let split = false;

  const die = createTravelDie(
    skin.id,
    getAttackDieFaceValues(dieId, damage, faceValues)
  );
  scene.add(die);
  const strike = createGuardianStrikeScene(scene, {
    target: enemy.position,
    scale: 0.65,
    name: 'core-finale-strike',
  });
  const strikeTiming = {
    combatId: 'core-finale',
    effectiveRoll: damage,
    launchAt: 2650,
    hitAt: 3200,
  };
  const aura = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: makeGlow(),
      color: '#b9fff2',
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    })
  );
  scene.add(aura);

  const rays = Array.from({ length: 8 }, (_, index) => {
    const ray = new THREE.Mesh(
      new THREE.PlaneGeometry(index % 2 ? 0.018 : 0.045, 1),
      glowMaterial(index % 2 ? '#fff1bc' : '#98fff2')
    );
    ray.rotation.z = (index * Math.PI) / 4;
    scene.add(ray);
    return ray;
  });
  const rings = Array.from({ length: 3 }, (_, index) => {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(1, index === 0 ? 0.025 : 0.012, 6, 96),
      glowMaterial(index === 0 ? '#c4fff2' : '#ffd689')
    );
    ring.rotation.set(index * 0.65, index * 0.42, 0);
    scene.add(ring);
    return ring;
  });

  const shards = new THREE.InstancedMesh(
    new THREE.TetrahedronGeometry(0.12),
    new THREE.MeshStandardMaterial({
      color: '#c9fff3',
      emissive: '#5ee4dc',
      emissiveIntensity: 1.1,
      metalness: 0.8,
      roughness: 0.2,
      transparent: true,
    }),
    112
  );
  shards.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  shards.frustumCulled = false;
  const fragment = new THREE.Object3D();
  const color = new THREE.Color();
  for (let index = 0; index < shards.count; index++) {
    color.set(['#b7fff3', '#ffe3a1', '#d7b9ff', '#89d8ff'][index % 4]);
    shards.setColorAt(index, color);
  }
  scene.add(shards);

  const lightningMaterial = new THREE.LineBasicMaterial({
    color: '#b4fff7',
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
  const lightning = Array.from({ length: 7 }, () => {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array(13 * 3), 3)
    );
    const line = new THREE.Line(geometry, lightningMaterial);
    line.frustumCulled = false;
    scene.add(line);
    return line;
  });

  const streakGeometry = new THREE.BufferGeometry();
  const streakPositions = new THREE.BufferAttribute(
    new Float32Array(120 * 6),
    3
  );
  streakGeometry.setAttribute('position', streakPositions);
  const streakMaterial = new THREE.LineBasicMaterial({
    color: '#a6fff0',
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const streaks = new THREE.LineSegments(streakGeometry, streakMaterial);
  streaks.frustumCulled = false;
  scene.add(streaks);

  const constellationGeometry = new THREE.BufferGeometry();
  const constellationPoints = [];
  for (let i = 0; i < 10; i++) {
    const angle = (i * Math.PI) / 5;
    const next = ((i + 1) * Math.PI) / 5;
    constellationPoints.push(
      Math.cos(angle) * 8,
      Math.sin(angle) * 5,
      -4,
      Math.cos(next) * 8,
      Math.sin(next) * 5,
      -4
    );
  }
  constellationGeometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(constellationPoints, 3)
  );
  const constellations = new THREE.LineSegments(
    constellationGeometry,
    new THREE.LineBasicMaterial({
      color: '#7ef8d9',
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
    })
  );
  scene.add(constellations);

  let available = true;
  const contextLost = (event) => {
    event.preventDefault();
    available = false;
    renderer.domElement.style.visibility = 'hidden';
  };
  renderer.domElement.addEventListener('webglcontextlost', contextLost);
  const stopObserving = observeElementSize(host, () => {
    const width = Math.max(1, host.clientWidth),
      height = Math.max(1, host.clientHeight);
    renderer.setPixelRatio(
      Math.min(
        window.devicePixelRatio || 1,
        1.5,
        Math.sqrt(1200000 / (width * height))
      )
    );
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    sky.resize(camera.aspect);
  });

  return {
    update(elapsed, frame) {
      if (!available) return false;
      const time = reduceMotion ? 9.9 : elapsed;
      const viewDistance = Math.max(10.5, 6.8 / camera.aspect);
      const retreat = frame.restore * 21;
      camera.position.set(
        reduceMotion ? 0 : Math.sin(time * 0.55) * 0.7 * (1 - frame.restore),
        0.8 + frame.restore * 1.5,
        viewDistance + retreat
      );
      // Keep the strike above the captions, including on portrait screens.
      camera.lookAt(0, -0.7, frame.hero * 3);
      sky.update(time, frame.restore, frame.charge * (1 - frame.restore));
      heart.update({
        elapsed: time * (1 - frame.crack * 0.8),
        evolution: 1,
        meltdown: 1,
        stage: 'bossCharge',
        progress: frame.charge,
        camera,
        reduceMotion,
        visible: !split && !reduceMotion,
      });
      if (!split && frame.crack > 0 && !reduceMotion) {
        scene.updateMatrixWorld(true);
        destruction.start(heart.body);
        split = true;
        heart.group.visible = false;
      }
      if (split) destruction.update(Math.max(0, time - 3.2), reduceMotion);
      destruction.group.visible = split && time < 7 && !reduceMotion;
      coreLight.intensity = (6 + frame.charge * 10) * (1 - frame.burst);
      coreLight.color.set(frame.crack > 0 ? '#a4fff0' : '#ff326d');

      die.position.set(
        0,
        0.3 + frame.hero * 2.5 + (reduceMotion ? 0 : Math.sin(time * 2) * 0.04),
        1.3 + frame.charge * 2.4 - frame.strike * 4.5 + frame.hero * 18
      );
      die.rotation.set(
        -0.22 + frame.strike * 0.45 - frame.hero * 0.5,
        THREE.MathUtils.lerp(
          0.35 + (reduceMotion ? 0 : time * 0.22) + frame.strike * Math.PI * 2,
          Math.PI * 2 + 0.42,
          frame.hero
        ),
        -0.12 + frame.strike * Math.PI * 2
      );
      die.scale.setScalar(0.9 + frame.charge * 0.25 + frame.hero * 3.7);
      strike.update({
        combat: strikeTiming,
        elapsed: time * 1000,
        die,
        camera,
        reduceMotion,
      });
      aura.position.copy(die.position);
      aura.position.z -= 0.6;
      aura.scale.setScalar(2 + frame.charge * 3.2 + frame.hero * 7);
      aura.material.opacity = 0.12 + frame.charge * 0.3 + frame.hero * 0.18;
      aura.material.color.set(frame.hero > 0 ? '#a4fff0' : '#ffde91');
      rays.forEach((ray, index) => {
        ray.visible = time > 1 && time < 5.4 && !reduceMotion;
        ray.position.copy(die.position);
        ray.quaternion.copy(camera.quaternion);
        ray.rotateZ((index * Math.PI) / 4 + time * 0.12);
        ray.scale.y = (2 + frame.charge * 9) * (1 - smooth(time, 4.3, 5.4));
        ray.material.opacity = frame.charge * 0.4 * (1 - frame.burst);
      });
      rings.forEach((ring, index) => {
        const age = Math.max(0, time - 4.15 - index * 0.15);
        ring.visible = age > 0 && time < 7.5 && !reduceMotion;
        ring.scale.setScalar(0.5 + age * (9 + index * 2));
        ring.material.opacity = Math.max(0, 0.85 - age * 0.3);
      });

      shards.visible = time >= 4.15 && time < 8.5 && !reduceMotion;
      const blastAge = Math.max(0, time - 4.15);
      shards.material.opacity = 1 - smooth(time, 6.4, 8.5);
      for (let index = 0; index < shards.count; index++) {
        const angle = index * 2.39996;
        const vertical = (index / shards.count - 0.5) * 2;
        const radius = 0.6 + blastAge * (1.8 + (index % 7) * 0.44);
        fragment.position.set(
          Math.cos(angle) * radius,
          vertical * radius * 0.8,
          Math.sin(angle) * radius
        );
        fragment.rotation.set(
          angle + blastAge * 2,
          vertical + blastAge * 3,
          angle * 0.4
        );
        fragment.scale.setScalar(
          (0.5 + (index % 4) * 0.3) * (1 - smooth(time, 7, 8.5))
        );
        fragment.updateMatrix();
        shards.setMatrixAt(index, fragment.matrix);
      }
      shards.instanceMatrix.needsUpdate = true;
      lightningMaterial.opacity = frame.charge * (1 - frame.burst) * 0.75;
      lightning.forEach((line, index) => {
        line.visible = time > 1.2 && time < 5.5 && !reduceMotion;
        const positions = line.geometry.attributes.position;
        const angle = index * 2.39996 + time * 0.18;
        for (let point = 0; point < positions.count; point++) {
          const progress = point / (positions.count - 1);
          const radius = progress * (2.6 + frame.charge * 1.4);
          const jitter =
            Math.sin(point * 12 + Math.floor(time * 10) * 3 + index) *
            0.12 *
            Math.sin(progress * Math.PI);
          positions.setXYZ(
            point,
            Math.cos(angle) * radius + jitter,
            0.3 + Math.sin(angle) * radius + jitter,
            1.5 - progress * 2
          );
        }
        positions.needsUpdate = true;
      });
      streaks.visible = time > 3.9 && time < 8.6 && !reduceMotion;
      streakMaterial.opacity =
        Math.sin(smooth(time, 3.9, 8.6) * Math.PI) * 0.55;
      for (let index = 0; index < 120; index++) {
        const angle = index * 2.39996;
        const radius = 1 + (index % 13) * 0.6;
        const travel = (blastAge * 9 + index * 0.8) % 30;
        streakPositions.setXYZ(
          index * 2,
          Math.cos(angle) * radius,
          Math.sin(angle) * radius,
          travel - 12
        );
        streakPositions.setXYZ(
          index * 2 + 1,
          Math.cos(angle) * radius * 1.1,
          Math.sin(angle) * radius * 1.1,
          travel - 9
        );
      }
      streakPositions.needsUpdate = true;
      constellations.material.opacity =
        frame.restore * (1 - frame.hero * 0.5) * 0.2;
      constellations.rotation.z = reduceMotion ? 0 : time * 0.015;
      renderer.render(scene, camera);
      return true;
    },
    dispose() {
      strike.dispose();
      stopObserving();
      renderer.domElement.removeEventListener('webglcontextlost', contextLost);
      const geometries = new Set(),
        materials = new Set(),
        textures = new Set();
      scene.traverse((object) => {
        if (object.geometry) geometries.add(object.geometry);
        if (object.material) {
          const surfaces = Array.isArray(object.material)
            ? object.material
            : [object.material];
          surfaces.forEach((surface) => {
            materials.add(surface);
            if (surface.map) textures.add(surface.map);
          });
        }
      });
      geometries.forEach((geometry) => geometry.dispose());
      materials.forEach((surface) => surface.dispose());
      textures.forEach((texture) => texture.dispose());
      renderer.dispose();
      renderer.forceContextLoss?.();
      renderer.domElement.remove();
    },
  };
};
